import React, {useEffect, useRef, useState} from 'react'

import {isNil} from 'es-toolkit'

import config from '../config'

import * as localStorage from '../../../commons/src/services/local-storage'
import {Filter, FilterParams} from '../libs/table-row-filter'

import useLocalStorageState from './use-local-storage-state'

export type FilterMap<T> = Record<string, Filter<T, FilterParams<T>>>

const useTableRowFilters = <TableRow>(
  initialFilters: FilterMap<TableRow> = {},
  storedFilterValuesKeys?: string[]
) => {
  const [storedFilterValues, setStoredFilterValues] = useLocalStorageState<Record<string, unknown>>(
    config.localStorageKeys.ordersFilterValues,
    {}
  )
  const [filterValueMap, setFilterValueMap] = useState<Record<string, unknown>>(storedFilterValues)

  const localStorageAvailable = localStorage.hasLocalStorage

  const getInitialFilters = () => {
    if (storedFilterValuesKeys && localStorageAvailable) {
      const initialFiltersWithStoredValues = {...initialFilters}

      storedFilterValuesKeys.forEach(key => {
        const persistedValue = storedFilterValues[key]
        if (!isNil(persistedValue)) {
          const filter = initialFilters[key]
          const newFilter = filter.createCopy(persistedValue)
          initialFiltersWithStoredValues[key] = newFilter
        }
      })
      return initialFiltersWithStoredValues
    }
    return initialFilters
  }
  const [filters, setFilters] = React.useState<FilterMap<TableRow>>(getInitialFilters())

  const filterValueMapRef = useRef(filterValueMap)
  filterValueMapRef.current = filterValueMap

  const setFilter = (
    key: string,
    newFilter: Filter<TableRow, FilterParams<TableRow>>,
    newFilterValue?: unknown
  ) => {
    setFilters({
      ...filters,
      [key]: newFilter,
    })

    if (storedFilterValuesKeys && storedFilterValuesKeys.includes(key) && localStorageAvailable) {
      setFilterValueMap({...filterValueMap, [key]: newFilterValue})
    }
  }

  const resetFilters = () => {
    setFilters(initialFilters)

    if (storedFilterValuesKeys && localStorageAvailable) {
      setFilterValueMap({})
    }
  }

  const hasActiveFilters = () =>
    Object.values(filters).reduce(
      (aggr, filter: Filter<TableRow, FilterParams<TableRow>>) => aggr || filter.isActive(),
      false
    )

  // Store filterValues when page unrenders
  useEffect(() => {
    return () => {
      if (storedFilterValuesKeys && localStorageAvailable) {
        setStoredFilterValues(filterValueMapRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    filters,
    hasActiveFilters,
    setFilter,
    resetFilters,
  }
}

export default useTableRowFilters
